import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { aiAssistantApi, MissedClockOutPage } from '../services/aiAssistantApi';

function readError(error: unknown): string {
  if (typeof error === 'object' && error && 'response' in error) {
    const data = (error as { response?: { data?: { message?: string } } }).response?.data;
    if (data?.message)
      return data.message;
  }
  return 'The missed clock-out list could not be retrieved.';
}

const MissedClockOutsPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const page = Math.max(1, Number(searchParams.get('page')) || 1);
  const pageSize = Math.min(100, Math.max(1, Number(searchParams.get('pageSize')) || 20));
  const [data, setData] = useState<MissedClockOutPage | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    aiAssistantApi.missedClockOuts(page, pageSize)
      .then((result) => {
        if (!cancelled)
          setData(result);
      })
      .catch((reason: unknown) => {
        if (!cancelled)
          setError(readError(reason));
      })
      .finally(() => {
        if (!cancelled)
          setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [page, pageSize]);

  const total = data?.total ?? 0;
  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);
  const hasNext = data != null && page * pageSize < total;

  const goTo = (nextPage: number) => {
    const next = new URLSearchParams(searchParams);
    next.set('page', String(nextPage));
    next.set('pageSize', String(pageSize));
    setSearchParams(next);
  };

  return (
    <div className="flex-1 overflow-y-auto bg-neutral-50">
      <div className="mx-auto max-w-3xl px-4 py-6 sm:px-6">
        <Link to="/ai-assistant" className="text-sm font-medium text-blue-700 underline">
          Back to Ask Staffly
        </Link>
        <h1 className="mt-3 text-2xl font-bold text-neutral-900">Missed clock-outs</h1>
        <p className="mt-1 text-sm text-neutral-600">
          Every punch that is still open, oldest clock-in first. This is the full list behind the Ask Staffly answer.
        </p>

        {loading && <p className="mt-6 text-sm text-neutral-500">Loading missed clock-outs…</p>}
        {error && <p className="mt-6 rounded-md bg-red-50 px-3 py-2 text-sm text-red-800">{error}</p>}

        {!loading && !error && data && (
          <>
            <p className="mt-6 text-sm font-medium text-neutral-900">
              {total === 1 ? '1 employee has not clocked out.' : `${total} employees have not clocked out.`}
            </p>
            {total > 0 && (
              <p className="mt-1 text-xs text-neutral-500">
                Showing {from}-{to} of {total}
              </p>
            )}
            {total === 0 ? (
              <p className="mt-4 text-sm text-neutral-500">No missed clock-outs were found for the locations you can see.</p>
            ) : (
              <ul className="mt-4 space-y-2">
                {data.items.map((row) => (
                  <li key={row.attendanceId} className="rounded-lg border border-red-200 bg-red-50 px-3 py-2">
                    <div className="font-medium text-neutral-900">{row.employeeName}</div>
                    <div className="mt-1 text-xs text-neutral-600">
                      {row.locationName} — clocked in {row.clockIn}
                    </div>
                  </li>
                ))}
              </ul>
            )}
            {total > pageSize && (
              <div className="mt-4 flex items-center gap-2">
                <button
                  type="button"
                  className="rounded-md border border-neutral-300 bg-white px-3 py-1.5 text-sm disabled:opacity-40"
                  onClick={() => goTo(page - 1)}
                  disabled={page <= 1 || loading}
                >
                  Previous
                </button>
                <button
                  type="button"
                  className="rounded-md border border-neutral-300 bg-white px-3 py-1.5 text-sm disabled:opacity-40"
                  onClick={() => goTo(page + 1)}
                  disabled={!hasNext || loading}
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default MissedClockOutsPage;
